
import { Component, Vue } from 'vue-property-decorator'
import { ListDepts, ListProjects } from '@/api/orgAdmin'
import { ListOrgUsers } from '@/api/orgAdmin'
import { BatchAssignRepairDept, ListLeaks } from '@/api/leaks'
import { ILeakPoint, IAllDeptInfo, IPoint, IAllUserInfo, DeptType, IProject, IDept, LeakStatus, IPipeDiameter } from '@/api/types'
// 引入Excel导出相关库
import * as XLSX from 'xlsx-js-style'
import saveAs from 'file-saver'
// 引入自定义Excel导出函数
import { leaks2DetailExcel, leaks2RepairSummaryExcel } from '@/utils/leak2Excel'

// 导入组件
import LeakList from '@/views/leaks/components/LeakList/index.vue'
import SearchFilter from '@/views/leaks/components/SearchFilter/index.vue'
import { HasPer, IsMobile } from '@/utils/check'
import { ListPipeDiameters } from '@/api/baseData'

@Component({
  name: 'LeakManagement',
  components: {
    LeakList,
    SearchFilter
  }
})
export default class extends Vue {
  // 对话框相关
  private exportDialogVisible = false
  private exportType = 'detail' // 导出类型，默认为明细表

  private shouldReloadList = true

  // 计算属性：是否为移动端
  get isMobile() {
    return IsMobile()
  }

  // 计算属性：是否有选择的漏点
  get hasSelectedLeaks(): boolean {
    return this.$refs.leakListRef ? (this.$refs.leakListRef as any).multipleSelection.length > 0 : false
  }

  // 搜索参数
  private searchParams = {
    status: this.getDefaultStatus(),
    keywords: [''] as string[],
    addressKeywords: [''] as string[],
    createTimeStart: '',
    createTimeEnd: '',
    reportTimeStart: '',
    reportTimeEnd: '',
    page: 1,
    pageSize: 30
  }

  // 选项数据
  private deptList: IAllDeptInfo[] = []
  private userList: IAllUserInfo[] = []
  private projectList: IProject[] = []
  private sizeInfos: IPipeDiameter[] = []

  // 操作按钮配置
  private actionConfig = {
    showDetail: true,
    showEdit: true,
    showReport: true,
    showAssign: true,
    showMarkNoNeed: true,
    showComplete: true
  }

  private getDefaultStatus() {
    return [
      LeakStatus.NeedAssignRepairDept,
      LeakStatus.NeedAssignRepairUser,
      LeakStatus.Repairing,
      LeakStatus.Repaired,
      LeakStatus.CantRepair,
      LeakStatus.NoNeedRepair
    ]
  }

  // 计算属性
  get hasBatchOperationPermission() {
    return HasPer(['漏点管理', '上帝']) && !this.isMobile
  }
  // 显示批量操作
  private showBatchOperations() {
    if (this.$refs.leakListRef) {
      (this.$refs.leakListRef as any).showBatchOperations = true
    }
  }

  created() {
    // 初始化数据
    this.getOptions()
    // 设置默认年度筛选（当前年份）
    const currentYear = new Date().getFullYear()
    this.searchParams.createTimeStart = `${currentYear}-01-01T00:00:00Z`
    this.searchParams.createTimeEnd = `${currentYear}-12-31T23:59:59Z`

    // 监听窗口大小变化
    window.addEventListener('resize', this.handleResize)
  }

  destroyed() {
    window.removeEventListener('resize', this.handleResize)
  }

  // 处理窗口大小变化
  private handleResize() {
  }

  private async getOptions() {
    await this.getDeptList()
    await this.getUserList()
    await this.getProjectList()
    await this.getSizeInfos()
  }

  private async getDeptList() {
    try {
      const response = await ListDepts()
      this.deptList = (response.data || [])
    } catch (error) {
      console.error('获取部门列表失败', error)
    }
  }

  private async getSizeInfos() {
    try {
      const response = await ListPipeDiameters()
      this.sizeInfos = response.data || []
    } catch (error) {
      console.error('获取管径列表失败', error)
    }
  }

  private get checkUsers() : IAllUserInfo[] {
    return this.userList.filter(user => user.permissions.includes('漏点检测'))
  }

  private get repairUsers() : IAllUserInfo[] {
    return this.userList.filter(user => user.permissions.includes('漏点维修'))
  }

  private get repairDepts() : IDept [] {
    return this.deptList.filter(dept => dept.dept.type === DeptType.Repair).map(dept=>dept.dept)
  }


  private async getUserList() {
    try {
      const { data } = await ListOrgUsers()
      this.userList = data.items || []
    } catch (error) {
      console.error('获取用户列表失败', error)
    }
  }

  private async getProjectList() {
      const { data } = await ListProjects()
      this.projectList = data.map((project:any) => project.project)
  }

  // 刷新列表
  private refreshList() {
    this.shouldReloadList = false
    this.$nextTick(() => {
      this.shouldReloadList = true
    })
  }

  // 处理搜索条件变化
  private handleFilterChange(params: any) {
    console.log('搜索条件变化:', params)
    this.searchParams = { ...params }
    this.refreshList()
  }

  // 处理搜索条件重置
  private handleFilterReset() {
    console.log('搜索条件已重置')
    // 由于重置也会触发filter-change事件，这里不需要额外处理
  }

  // 处理地图标记点击
  private handleMapMarkerClick(leakPoint: ILeakPoint) {
    this.handleDetail(leakPoint.id, 'view')
  }

  // 处理详情 - 改为页面导航方式
  private handleDetail(leakId: number, operationType?: string) {
    if (!leakId) {
      this.$message.warning('无效的漏点ID')
      return
    }

    // 默认为查看模式
    const mode = operationType || 'view'

    // 导航到详情页面
    this.$router.push({
      name: 'LeakDetail',
      params: {
        id: leakId.toString(),
        mode: mode,
        projects: JSON.stringify(this.projectList),
        checkUsers: JSON.stringify(this.checkUsers),
        repairUsers: JSON.stringify(this.repairUsers),
        repairDepts: JSON.stringify(this.repairDepts),
      }
    })
  }

  // 处理上报
  private handleReport(leakId: number) {
    this.handleDetail(leakId, 'report')
  }

  // 处理分配
  private handleAssign(leakId: number) {
    this.handleDetail(leakId, 'assign')
  }

  // 处理完成
  private handleFinish(leak: ILeakPoint) {
    this.refreshList()
  }

  // 创建漏点 - 使用页面导航代替弹窗
  private navigateToCreatePage(data?: { position: IPoint, address: string }) {
    // 将检测人员列表转为JSON字符串
    const detectUsersJson = encodeURIComponent(JSON.stringify(this.checkUsers))

    // 保存位置信息
    let locationData = {}
    if (data && data.position) {
      locationData = {
        position: data.position,
        address: data.address || ''
      }
    }
    const locationJson = encodeURIComponent(JSON.stringify(locationData))

    // 导航到创建漏点页面
    this.$router.push({
      name: 'CreateLeak',
      query: {
        detectUsers: detectUsersJson,
        locationData: locationJson
      }
    })
  }

  // 处理批量分配
  private async handleBatchAssign(data: { leakIds: number[], deptId: string | number }) {
    if (!data.leakIds.length) {
      this.$message({
        message: '没有可分配的漏点',
        type: 'warning'
      })
      return
    }

    try {
      // 显示加载提示
      this.$message({
        message: '正在批量分配漏点...',
        type: 'info'
      })


      // 筛选出当前仍处于待分配状态的漏点
      const validLeakIds = data.leakIds

      // 调用批量分配API
      await BatchAssignRepairDept({
        leakIds: validLeakIds,
        deptId: Number(data.deptId)
      })

      // 操作成功提示
      this.$message({
        message: `成功将${validLeakIds.length}个漏点分配给维修部门`,
        type: 'success'
      })

      // 刷新列表
      this.refreshList()
    } catch (error) {
      // 处理错误
      console.error('批量分配漏点失败', error)
      this.$message({
        message: '批量分配失败，请重试',
        type: 'error'
      })
    }
  }

  // 处理Excel导出按钮点击
  private handleExportExcel() {
    // 显示导出类型选择对话框
    this.exportDialogVisible = true
  }

  // 执行导出操作
  private async executeExport() {
    try {
      // 关闭对话框
      this.exportDialogVisible = false

      // 根据导出类型执行不同的导出操作
      if (this.exportType === 'summary') {
        await this.exportSummaryExcel()
      } else {
        await this.exportDetailExcel()
      }
    } catch (error) {
      console.error('导出Excel失败', error)
      this.$message({
        message: '导出Excel失败，请重试',
        type: 'error'
      })
    }
  }

  // 导出汇总表
  private async exportSummaryExcel() {
    try {
      // 获取当前选中的漏点ID列表
      const leakListRef = this.$refs.leakListRef as any
      let selectedLeakIds: number[] = []
      let exportMode = '全部';

      if (leakListRef && leakListRef.multipleSelection && leakListRef.multipleSelection.length > 0) {
        const selectedLeaks = leakListRef.multipleSelection as ILeakPoint[]
        selectedLeakIds = selectedLeaks.map(leak => leak.id)
        exportMode = '选中';
      }

      // 显示导出中提示
      this.$message({
        message: exportMode === '全部' ? '正在准备导出所有漏点结算数据...' : '正在准备导出选中漏点结算数据...',
        type: 'info'
      })

      // 准备获取漏点数据
      let leakData: any;

      if (exportMode === '全部') {
        // 导出当前筛选条件下的所有漏点
        const { data } = await ListLeaks({
          ...this.searchParams,
          pageSize: 1000, // 设置较大的数量以获取更多数据
          page: 1
        });
        leakData = data;
      } else {
        // 获取选中的漏点列表
        leakData = {
          items: leakListRef.multipleSelection,
          total: leakListRef.multipleSelection.length
        };
      }

      if (!leakData || !leakData.items || leakData.items.length === 0) {
        this.$message({
          message: '未能获取到漏点数据',
          type: 'error'
        })
        return
      }

      // 使用leak2Excel中的函数生成工作簿
      const wb = leaks2RepairSummaryExcel(leakData.items, this.sizeInfos);

      // 导出Excel文件
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
      const blob = new Blob([excelBuffer], { type: 'application/octet-stream' })

      // 生成文件名（当前日期+时间）
      const now = new Date()
      const dateTimeStr = `${now.getFullYear()}${(now.getMonth()+1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}_${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}`
      const fileName = exportMode === '全部'
        ? `漏点结算数据全部导出_${dateTimeStr}.xlsx`
        : `漏点结算数据选择导出_${dateTimeStr}.xlsx`

      // 保存文件
      saveAs(blob, fileName)

      // 提示用户导出成功
      this.$message({
        message: exportMode === '全部'
          ? `成功导出${leakData.items.length}条漏点结算数据`
          : `成功导出${leakData.items.length}条已选择的漏点结算数据`,
        type: 'success'
      })
    } catch (error) {
      console.error('导出结算表失败', error)
      this.$message({
        message: '导出结算表失败，请重试',
        type: 'error'
      })
    }
  }

  // 导出明细表 (原来的导出功能)
  private async exportDetailExcel() {
    try {
      // 获取当前选中的漏点ID列表
      const leakListRef = this.$refs.leakListRef as any
      let selectedLeakIds: number[] = []
      let exportMode = '全部';

      if (leakListRef && leakListRef.multipleSelection && leakListRef.multipleSelection.length > 0) {
        const selectedLeaks = leakListRef.multipleSelection as ILeakPoint[]
        selectedLeakIds = selectedLeaks.map(leak => leak.id)
        exportMode = '选中';
      }

      // 显示导出中提示
      this.$message({
        message: exportMode === '全部' ? '正在准备导出所有漏点明细数据...' : '正在准备导出选中漏点明细数据...',
        type: 'info'
      })

      // 准备获取漏点数据
      let leakData: any;

      if (exportMode === '全部') {
        // 导出当前筛选条件下的所有漏点
        const { data } = await ListLeaks({
          ...this.searchParams,
          pageSize: 1000, // 设置较大的数量以获取更多数据
          page: 1
        });
        leakData = data;
      } else {
        // 获取选中的漏点列表
        leakData = {
          items: leakListRef.multipleSelection,
          total: leakListRef.multipleSelection.length
        };
      }

      if (!leakData || !leakData.items || leakData.items.length === 0) {
        this.$message({
          message: '未能获取到漏点数据',
          type: 'error'
        })
        return
      }

      // 使用leak2Excel中的函数生成工作簿
      const wb = leaks2DetailExcel(leakData.items);

      // 导出Excel文件
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
      const blob = new Blob([excelBuffer], { type: 'application/octet-stream' })

      // 生成文件名（当前日期+时间）
      const now = new Date()
      const dateTimeStr = `${now.getFullYear()}${(now.getMonth()+1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}_${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}`
      const fileName = exportMode === '全部'
        ? `漏点明细数据全部导出_${dateTimeStr}.xlsx`
        : `漏点明细数据选择导出_${dateTimeStr}.xlsx`

      // 保存文件
      saveAs(blob, fileName)

      // 提示用户导出成功
      this.$message({
        message: exportMode === '全部'
          ? `成功导出${leakData.items.length}条漏点明细数据`
          : `成功导出${leakData.items.length}条已选择的漏点明细数据`,
        type: 'success'
      })
    } catch (error) {
      console.error('导出明细表失败', error)
      this.$message({
        message: '导出明细表失败，请重试',
        type: 'error'
      })
    }
  }
}
